import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { CATEGORIAS_CONTEUDO_LABELS } from '@/pages/painel/lib/inteligencia';

interface ConteudoData {
  agrupado: { categoria: string; total: number; conversas: number }[];
  metricas: {
    conversas_total: number;
    contatos_unicos: number;
    evolucao_mensal: Record<string, number>;
  };
  aguardando_dados: boolean;
}

const FORMATOS = ['Instagram', 'FAQ', 'blog/site', 'material comercial', 'script de vendedor'];

export default function InteligenciaConteudo() {
  const { password } = useOutletContext<{ password: string }>();
  const [data, setData] = useState<ConteudoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formato, setFormato] = useState('Instagram');
  const [sugestao, setSugestao] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'conteudo', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (resp?.error) throw new Error(resp.error);
      setData(resp as ConteudoData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar inteligência de conteúdo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const sugerir = () => {
    if (data?.aguardando_dados) {
      setSugestao('Ainda não há mensagens importadas. As sugestões serão baseadas nas perguntas reais dos clientes (preço, prazo, frete, etc.) assim que os CSVs dos dois WhatsApps forem carregados.');
      return;
    }
    setSugestao(`Sugestão de ${formato} em preparação — será gerada a partir do agrupamento real de perguntas dos contatos.`);
  };

  const totalMencoes = (data?.agrupado ?? []).reduce((s, c) => s + c.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-teal-50 text-teal-700 rounded-xl">
            <i className="ri-question-answer-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">O que meus clientes estão perguntando</h2>
            <p className="text-sm text-gray-500">Perguntas recorrentes → conteúdo e material comercial direcionado.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60">
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i> Atualizar
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && !data ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <>
          {/* Métricas */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Conversas', value: String(data.metricas.conversas_total), icon: 'ri-chat-3-line' },
              { label: 'Contatos únicos', value: String(data.metricas.contatos_unicos), icon: 'ri-team-line' },
              { label: 'Menções a tópicos', value: String(totalMencoes), icon: 'ri-price-tag-3-line' },
            ].map((k) => (
              <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5"><i className={k.icon}></i>{k.label}</p>
                <p className="text-base font-bold text-gray-900">{k.value}</p>
              </div>
            ))}
          </div>

          {data.aguardando_dados ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-gray-50 text-gray-400 mb-3">
                <i className="ri-chat-smile-3-line text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-gray-700">Aguardando mensagens reais</p>
              <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                O agrupamento por tema (preço, prazo, frete, garantia, montagem, projeto...) será preenchido quando os
                CSVs dos dois WhatsApps forem importados. Nenhuma mensagem foi processada nesta fase.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.agrupado.map((c) => (
                <div key={c.categoria} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">{CATEGORIAS_CONTEUDO_LABELS[c.categoria] ?? c.categoria}</span>
                    <span className="text-xs font-bold text-[#E8610A]">{c.total}</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full" style={{ width: `${totalMencoes ? (c.total / totalMencoes) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sugerir conteúdo */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h4 className="text-sm font-bold text-gray-900 mb-3">Sugerir conteúdo</h4>
            <div className="flex items-center gap-3 flex-wrap">
              <select value={formato} onChange={(e) => setFormato(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                {FORMATOS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <button onClick={sugerir} className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap">
                <i className="ri-magic-line"></i>
                Sugerir conteúdo
              </button>
            </div>
            {sugestao && (
              <p className="text-sm text-gray-600 mt-3 bg-gray-50 rounded-lg px-4 py-3">{sugestao}</p>
            )}
            <p className="text-xs text-gray-400 mt-3">
              Toda sugestão explica a base real (ex.: "baseado em 38 contatos únicos perguntando sobre prazo nos últimos 90 dias").
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}